import { ALL_CUBE_FACES } from './constants';
import { SpatialIndex } from './SpatialIndex';
import { TextureCache } from './TextureCache';
import { TileLoadQueue } from './TileLoadQueue';
import { LODController } from './LODController';
import { ViewportService } from './ViewportService';
import { TransitionCoordinator } from './TransitionCoordinator';
import { SceneAdapter } from './SceneAdapter';

/**
 * Debug configuration
 */
export interface DebugConfig {
  enabled: boolean;
  logLevel: 'error' | 'warn' | 'info' | 'debug';
  enableMetrics: boolean;
  enableTracing: boolean;
  metricsInterval: number;
}

/**
 * Debug metrics snapshot
 */
export interface DebugMetrics {
  timestamp: number;
  spatialIndex: {
    spatialAreas: number;
    totalTiles: number;
    visibleTiles: number;
    tilesByLevel: Record<number, number>;
    tilesByFace: Record<string, number>;
  };
  textureCache: {
    size: number;
    maxSize: number;
    totalRefs: number;
    memoryEstimate: number;
  };
  loadQueue: {
    queueSize: number;
    activeLoads: number;
    completedLoads: number;
    failedLoads: number;
    cancelledLoads: number;
  };
  lodController: {
    currentLevel: number;
    targetLevel: number;
    transitionState: string;
    transitionDuration: number;
    totalTransitions: number;
  };
  viewportService: {
    timeSinceLastUpdate: number;
    totalUpdates: number;
    throttledUpdates: number;
  };
  transitionCoordinator: {
    isTransitioning: boolean;
    pendingRemovals: number;
    completedTransitions: number;
    safetyCheckFailures: number;
  };
  sceneAdapter: {
    totalSceneTiles: number;
    tilesByLevel: Record<number, number>;
    tilesByFace: Record<string, number>;
  };
}

/**
 * Debug trace event
 */
export interface DebugTraceEvent {
  timestamp: number;
  level: string;
  category: string;
  message: string;
  data?: any;
}

/**
 * Debug and metrics collection for tile management
 */
export class Debug {
  private config: DebugConfig = {
    enabled: false,
    logLevel: 'warn',
    enableMetrics: true,
    enableTracing: false,
    metricsInterval: 5000, // 5 seconds
  };

  private readonly spatialIndex: SpatialIndex;
  private readonly textureCache: TextureCache;
  private readonly loadQueue: TileLoadQueue;
  private readonly lodController: LODController;
  private readonly viewportService: ViewportService;
  private readonly transitionCoordinator: TransitionCoordinator;
  private readonly sceneAdapter: SceneAdapter;

  private metricsHistory: DebugMetrics[] = [];
  private traceEvents: DebugTraceEvent[] = [];
  private metricsIntervalId: number | undefined;
  private readonly maxHistorySize = 100;
  private readonly maxTraceEvents = 1000;

  constructor(
    spatialIndex: SpatialIndex,
    textureCache: TextureCache,
    loadQueue: TileLoadQueue,
    lodController: LODController,
    viewportService: ViewportService,
    transitionCoordinator: TransitionCoordinator,
    sceneAdapter: SceneAdapter
  ) {
    this.spatialIndex = spatialIndex;
    this.textureCache = textureCache;
    this.loadQueue = loadQueue;
    this.lodController = lodController;
    this.viewportService = viewportService;
    this.transitionCoordinator = transitionCoordinator;
    this.sceneAdapter = sceneAdapter;
  }

  /**
   * Update debug configuration
   */
  configure(config: Partial<DebugConfig>): void {
    const oldEnabled = this.config.enabled;
    this.config = { ...this.config, ...config };

    // Start/stop metrics collection based on enabled state
    if (this.config.enabled && !oldEnabled) {
      this.startMetricsCollection();
    } else if (!this.config.enabled && oldEnabled) {
      this.stopMetricsCollection();
    }
  }

  /**
   * Start collecting metrics at regular intervals
   */
  private startMetricsCollection(): void {
    if (this.metricsIntervalId) {
      clearInterval(this.metricsIntervalId);
    }

    if (this.config.enableMetrics) {
      this.metricsIntervalId = window.setInterval(() => {
        this.collectMetrics();
      }, this.config.metricsInterval);

      // Collect initial metrics
      this.collectMetrics();
    }
  }

  /**
   * Stop metrics collection
   */
  private stopMetricsCollection(): void {
    if (this.metricsIntervalId) {
      clearInterval(this.metricsIntervalId);
      this.metricsIntervalId = undefined;
    }
  }

  /**
   * Collect current metrics snapshot
   */
  private collectMetrics(): void {
    const spatialStats = this.spatialIndex.getStats();
    const cacheStats = this.textureCache.getStats();
    const queueStats = this.loadQueue.getStats();
    const lodStats = this.lodController.getStats();
    const viewportStats = this.viewportService.getStats();
    const transitionStats = this.transitionCoordinator.getStats();
    const sceneTilesByLevel = this.sceneAdapter.countTileMeshesByLevel();
    const sceneTilesByFace = this.sceneAdapter.countTileMeshesByFace();

    const metrics: DebugMetrics = {
      timestamp: Date.now(),
      spatialIndex: {
        spatialAreas: spatialStats.spatialAreas,
        totalTiles: spatialStats.totalTiles,
        visibleTiles: spatialStats.visibleTiles,
        tilesByLevel: spatialStats.tilesByLevel,
        tilesByFace: spatialStats.tilesByFace,
      },
      textureCache: {
        size: cacheStats.size,
        maxSize: cacheStats.maxSize,
        totalRefs: cacheStats.totalRefs,
        memoryEstimate: cacheStats.memoryEstimate,
      },
      loadQueue: {
        queueSize: queueStats.queueSize,
        activeLoads: queueStats.activeLoads,
        completedLoads: queueStats.completedLoads,
        failedLoads: queueStats.failedLoads,
        cancelledLoads: queueStats.cancelledLoads,
      },
      lodController: {
        currentLevel: lodStats.currentLevel,
        targetLevel: lodStats.targetLevel,
        transitionState: lodStats.transitionState,
        transitionDuration: lodStats.transitionDuration,
        totalTransitions: lodStats.totalTransitions,
      },
      viewportService: {
        timeSinceLastUpdate: viewportStats.timeSinceLastUpdate,
        totalUpdates: viewportStats.totalUpdates,
        throttledUpdates: viewportStats.throttledUpdates,
      },
      transitionCoordinator: {
        isTransitioning: transitionStats.isTransitioning,
        pendingRemovals: transitionStats.pendingRemovals,
        completedTransitions: transitionStats.completedTransitions,
        safetyCheckFailures: transitionStats.safetyCheckFailures,
      },
      sceneAdapter: {
        totalSceneTiles: Object.values(sceneTilesByLevel).reduce((sum, count) => sum + count, 0),
        tilesByLevel: sceneTilesByLevel,
        tilesByFace: sceneTilesByFace,
      },
    };

    this.metricsHistory.push(metrics);

    // Keep history size bounded
    if (this.metricsHistory.length > this.maxHistorySize) {
      this.metricsHistory.shift();
    }

    this.trace('metrics', 'Metrics collected', metrics);
  }

  /**
   * Log debug message
   */
  log(level: 'error' | 'warn' | 'info' | 'debug', category: string, message: string, data?: any): void {
    if (!this.config.enabled) return;

    // Check log level
    const levels = ['error', 'warn', 'info', 'debug'];
    const currentLevelIndex = levels.indexOf(this.config.logLevel);
    const messageLevelIndex = levels.indexOf(level);

    if (messageLevelIndex > currentLevelIndex) return;

    // Log to console
    const timestamp = new Date().toISOString();
    const logMessage = `[${timestamp}] [${level.toUpperCase()}] [${category}] ${message}`;
    
    switch (level) {
      case 'error':
        console.error(logMessage, data);
        break;
      case 'warn':
        console.warn(logMessage, data);
        break;
      case 'info':
        console.info(logMessage, data);
        break;
      case 'debug':
        console.debug(logMessage, data);
        break;
    }

    // Add to trace if enabled
    if (this.config.enableTracing) {
      this.trace(category, message, data);
    }
  }

  /**
   * Add trace event
   */
  private trace(category: string, message: string, data?: any): void {
    if (!this.config.enableTracing) return;

    const event: DebugTraceEvent = {
      timestamp: Date.now(),
      level: 'trace',
      category,
      message,
      data,
    };

    this.traceEvents.push(event);

    // Keep trace events bounded
    if (this.traceEvents.length > this.maxTraceEvents) {
      this.traceEvents.shift();
    }
  }

  /**
   * Get current metrics
   */
  getCurrentMetrics(): DebugMetrics | null {
    return this.metricsHistory.length > 0 
      ? this.metricsHistory[this.metricsHistory.length - 1] 
      : null;
  }

  /**
   * Get metrics history
   */
  getMetricsHistory(): DebugMetrics[] {
    return [...this.metricsHistory];
  }

  /**
   * Get trace events
   */
  getTraceEvents(): DebugTraceEvent[] {
    return [...this.traceEvents];
  }

  /**
   * Get debug summary
   */
  getDebugSummary(): string {
    const current = this.getCurrentMetrics();
    if (!current) {
      return 'No metrics available';
    }

    const lines = [
      '=== Tile Manager Debug Summary ===',
      `Timestamp: ${new Date(current.timestamp).toISOString()}`,
      '',
      '--- Spatial Index ---',
      `Spatial Areas: ${current.spatialIndex.spatialAreas}`,
      `Total Tiles: ${current.spatialIndex.totalTiles}`,
      `Visible Tiles: ${current.spatialIndex.visibleTiles}`,
      `Tiles by Level: ${JSON.stringify(current.spatialIndex.tilesByLevel)}`,
      `Tiles by Face: ${JSON.stringify(current.spatialIndex.tilesByFace)}`,
      '',
      '--- Texture Cache ---',
      `Cache Size: ${current.textureCache.size}/${current.textureCache.maxSize}`,
      `Total References: ${current.textureCache.totalRefs}`,
      `Memory Estimate: ${(current.textureCache.memoryEstimate / 1024 / 1024).toFixed(1)} MB`,
      '',
      '--- Load Queue ---',
      `Queue Size: ${current.loadQueue.queueSize}`,
      `Active Loads: ${current.loadQueue.activeLoads}`,
      `Completed: ${current.loadQueue.completedLoads}`,
      `Failed: ${current.loadQueue.failedLoads}`,
      `Cancelled: ${current.loadQueue.cancelledLoads}`,
      '',
      '--- LOD Controller ---',
      `Current Level: ${current.lodController.currentLevel}`,
      `Target Level: ${current.lodController.targetLevel}`,
      `State: ${current.lodController.transitionState}`,
      `Transition Duration: ${current.lodController.transitionDuration}ms`,
      `Total Transitions: ${current.lodController.totalTransitions}`,
      '',
      '--- Viewport Service ---',
      `Time Since Last Update: ${current.viewportService.timeSinceLastUpdate}ms`,
      `Total Updates: ${current.viewportService.totalUpdates}`,
      `Throttled Updates: ${current.viewportService.throttledUpdates}`,
      '',
      '--- Transition Coordinator ---',
      `Is Transitioning: ${current.transitionCoordinator.isTransitioning}`,
      `Pending Removals: ${current.transitionCoordinator.pendingRemovals}`,
      `Completed Transitions: ${current.transitionCoordinator.completedTransitions}`,
      `Safety Check Failures: ${current.transitionCoordinator.safetyCheckFailures}`,
      '',
      '--- Scene Adapter ---',
      `Total Scene Tiles: ${current.sceneAdapter.totalSceneTiles}`,
      `Scene Tiles by Level: ${JSON.stringify(current.sceneAdapter.tilesByLevel)}`,
      `Scene Tiles by Face: ${JSON.stringify(current.sceneAdapter.tilesByFace)}`,
    ];

    return lines.join('\n');
  }

  /**
   * Debug method: Log current tile status (legacy compatibility)
   */
  debugTileStatus(): void {
    this.log('debug', 'tile-status', 'Current tile status', this.getCurrentMetrics());
  }

  /**
   * Debug method: Test spatial key generation (legacy compatibility)
   */
  debugSpatialKeyGeneration(): void {
    this.log('debug', 'spatial-keys', 'Testing spatial key generation');
    // Implementation would test key generation logic
  }

  /**
   * Debug method: Clean overlapping tiles (legacy compatibility)
   */
  debugCleanOverlappingTiles(): void {
    this.log('debug', 'cleanup', 'Manual cleanup of overlapping tiles requested');
    // This would trigger cleanup logic if needed
  }

  /**
   * Debug method: Get detailed scene tile information (legacy compatibility)
   */
  debugSceneTileInfo(): void {
    const validation = this.sceneAdapter.validateScene();
    this.log('debug', 'scene-info', 'Scene validation result', validation);
  }

  /**
   * Debug method: Remove level 1 for face (legacy compatibility)
   */
  debugRemoveLevel1ForFace(face: string): void {
    this.log('debug', 'level1-removal', `Manual level 1 removal requested for face ${face}`);
    this.sceneAdapter.forceRemoveAllLevel1TilesForFace(face);
  }

  /**
   * Export debug data for analysis
   */
  exportDebugData(): string {
    const exportData = {
      config: this.config,
      currentMetrics: this.getCurrentMetrics(),
      metricsHistory: this.getMetricsHistory(),
      traceEvents: this.getTraceEvents(),
      summary: this.getDebugSummary(),
    };

    return JSON.stringify(exportData, null, 2);
  }

  /**
   * Clear all debug data
   */
  clearDebugData(): void {
    this.metricsHistory.length = 0;
    this.traceEvents.length = 0;
  }

  /**
   * Get performance analysis
   */
  getPerformanceAnalysis(): {
    averageTransitionTime: number;
    cacheHitRate: number;
    loadSuccessRate: number;
    memoryEfficiency: number;
  } {
    const current = this.getCurrentMetrics();
    if (!current) {
      return {
        averageTransitionTime: 0,
        cacheHitRate: 0,
        loadSuccessRate: 0,
        memoryEfficiency: 0,
      };
    }

    const totalLoads = current.loadQueue.completedLoads + current.loadQueue.failedLoads;
    const loadSuccessRate = totalLoads > 0 ? current.loadQueue.completedLoads / totalLoads : 1;
    
    const cacheHitRate = current.textureCache.totalRefs > 0 
      ? current.textureCache.size / current.textureCache.totalRefs 
      : 0;

    const memoryEfficiency = current.textureCache.maxSize > 0 
      ? current.textureCache.size / current.textureCache.maxSize 
      : 0;

    return {
      averageTransitionTime: current.lodController.transitionDuration,
      cacheHitRate,
      loadSuccessRate,
      memoryEfficiency,
    };
  }

  /**
   * Dispose debug resources
   */
  dispose(): void {
    this.stopMetricsCollection();
    this.clearDebugData();
  }
}
